import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/axios';
import Layout from '../../common/components/Navbar';
import {
  Title,
  Group,
  Button,
  Paper,
  TextInput,
  Table,
  Badge,
  ActionIcon,
  Tooltip,
  Modal,
  Select,
  Grid,
  Text,
  Box,
  ScrollArea,
  Tabs,
  Stack,
  Checkbox,
  LoadingOverlay,
  PinInput,
  Center,
  Loader,
  Pagination // Added Pagination
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { 
  IconEdit, 
  IconPlus, 
  IconSearch, 
  IconFolder, 
  IconArchive,
  IconRotate2,
  IconMail,
  IconFilter,
  IconMoodEmpty
} from '@tabler/icons-react';
import Swal from 'sweetalert2';

const Residents = () => {
  const [residents, setResidents] = useState([]);
  const [archivedResidents, setArchivedResidents] = useState([]);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStreet, setFilterStreet] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [opened, { open, close }] = useDisclosure(false);
  const [docOpened, { open: openDoc, close: closeDoc }] = useDisclosure(false);
  const [otpOpened, { open: openOtp, close: closeOtp }] = useDisclosure(false); 
  const [activeTab, setActiveTab] = useState('active');
  const [isEdit, setIsEdit] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [selectedResidentName, setSelectedResidentName] = useState('');
  
  // Pagination
  const [activePage, setActivePage] = useState(1);
  const ITEMS_PER_PAGE = 50;
  
  const [residentInvoices, setResidentInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);

  const [loading, setLoading] = useState(false);

  const [selectedArchived, setSelectedArchived] = useState([]);

  const [otpInput, setOtpInput] = useState('');
  const [isOtpSending, setIsOtpSending] = useState(false);
  const [originalEmail, setOriginalEmail] = useState(''); 

  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    contactNumber: '',
    email: '',
    block: '',
    lot: '',
    street: '',
    houseNo: '', 
    type: 'homeowner',
    status: 'good_standing'
  });

  const streetOptions = [
    'Aquarius', 'Cancer', 'Capricorn', 'Gemini', 'Leo', 'Libra', 
    'Pisces', 'Sagittarius', 'Scorpio', 'Taurus', 'Virgo'
  ];

  const fetchResidents = useCallback(async () => {
    try {
      const params = {};
      if (filterStreet) params.street = filterStreet;
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;

      const { data } = await axios.get('/api/residents', { params });
      setResidents(data);
    } catch (error) { console.error(error); }
  }, [filterStreet, filterType, filterStatus]);

  const fetchArchivedResidents = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/residents/archive');
      setArchivedResidents(data);
    } catch (error) { console.error(error); }
  }, []);

  const fetchResidentInvoices = async (residentId) => {
      setInvoicesLoading(true);
      try {
          const { data } = await axios.get('/api/billing/invoices');
          const filteredInvoices = data.filter(inv => 
            inv.resident && inv.resident._id === residentId
          );
          setResidentInvoices(filteredInvoices);
      } catch (error) {
          console.error("Error fetching resident invoices:", error);
          setResidentInvoices([]);
      }
      setInvoicesLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'active') fetchResidents();
    else fetchArchivedResidents();
  }, [activeTab, fetchResidents, fetchArchivedResidents]); 

  // Reset pagination on tab or search change
  useEffect(() => {
    setActivePage(1);
  }, [activeTab, searchTerm]);

  const filterData = (data) => {
    return data.filter((r) => 
      r.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.address.block && r.address.block.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  const currentData = activeTab === 'active' ? filterData(residents) : filterData(archivedResidents);

  // Pagination Logic
  const paginatedData = currentData.slice(
    (activePage - 1) * ITEMS_PER_PAGE,
    activePage * ITEMS_PER_PAGE
  );

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'firstName' || name === 'lastName' || name === 'middleName') {
        if (value.length <= 50 && /^[a-zA-Z\s]*$/.test(value)) {
            setFormData({ ...formData, [name]: value });
        }
    } else if (name === 'block' || name === 'lot' || name === 'houseNo') {
        if (value.length <= 2 && /^\d*$/.test(value)) setFormData({ ...formData, [name]: value });
    } else if (name === 'contactNumber') {
        if (value.length <= 11 && /^\d*$/.test(value)) setFormData({ ...formData, [name]: value });
    } else if (name === 'email') {
        if (value.length <= 100) setFormData({ ...formData, [name]: value });
    } else {
        setFormData({ ...formData, [name]: value });
    }
  };

  const handleSelectChange = (name, value) => setFormData({ ...formData, [name]: value });

  const handleOpenModal = (resident = null) => {
    if (resident) {
      setIsEdit(true);
      setCurrentId(resident._id);
      setOriginalEmail(resident.email || ''); 
      setFormData({
        firstName: resident.firstName,
        middleName: resident.middleName || '',
        lastName: resident.lastName,
        contactNumber: resident.contactNumber,
        email: resident.email || '',
        block: resident.address.block || '',
        lot: resident.address.lot || '',
        street: resident.address.street,
        houseNo: resident.address.houseNo || '',
        type: resident.type,
        status: resident.status
      });
    } else {
      setIsEdit(false);
      setOriginalEmail('');
      setFormData({
        firstName: '', middleName: '', lastName: '', contactNumber: '', email: '',
        block: '', lot: '', street: '', houseNo: '',
        type: 'homeowner', status: 'good_standing'
      });
    }
    open();
  };

  const handleSendOtp = async () => {
    if (!formData.firstName || !formData.lastName) return Swal.fire('Error', 'Names required', 'warning');
    if (!formData.houseNo || !formData.street) return Swal.fire('Error', 'Address required', 'warning');
    if (formData.contactNumber.length !== 11) return Swal.fire('Error', 'Invalid contact', 'warning');
    
    setIsOtpSending(true);
    try {
        await axios.post('/api/residents/send-otp', { email: formData.email });
        Swal.fire({ icon: 'success', title: 'OTP Sent', timer: 1500, showConfirmButton: false });
        openOtp(); 
    } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'Failed', 'error');
    } finally {
        setIsOtpSending(false);
    }
  };

  const handleVerifyOtpAndSubmit = async () => {
    if (otpInput.length !== 6) return Swal.fire('Invalid OTP', 'Enter 6 digits', 'warning');
    handleSubmit(otpInput);
  };

  const handleSubmit = async (otpCode = null) => {
    try {
      const payload = {
        firstName: formData.firstName,
        middleName: formData.middleName, 
        lastName: formData.lastName,
        contactNumber: formData.contactNumber,
        email: formData.email,
        address: { block: formData.block, lot: formData.lot, street: formData.street, houseNo: formData.houseNo },
        type: formData.type,
        status: formData.status,
        otp: otpCode 
      };

      setLoading(true);

      if (isEdit) {
        if (formData.email !== originalEmail) {
            if (!otpCode) {
                setLoading(false); handleSendOtp(); return; 
            }
        }
        await axios.put(`/api/residents/${currentId}`, payload);
        Swal.fire('Success', 'Resident updated', 'success');
        closeOtp(); setOtpInput('');
      } else {
        if (!otpCode) {
            setLoading(false); return Swal.fire('Verify Email', 'OTP required', 'warning');
        }
        await axios.post('/api/residents', payload);
        Swal.fire('Success', 'Resident added!', 'success');
        closeOtp(); setOtpInput('');
      }
      
      if (activeTab === 'active') fetchResidents(); else fetchArchivedResidents();
      close();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (id) => {
    const result = await Swal.fire({ title: 'Archive?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33' });
    if (result.isConfirmed) {
      try { await axios.put(`/api/residents/${id}/archive`); Swal.fire('Archived!', '', 'success'); fetchResidents(); } 
      catch (error) { Swal.fire('Error', 'Failed', 'error'); }
    }
  };

  const handleRestore = async (id) => {
    const result = await Swal.fire({ title: 'Restore?', icon: 'question', showCancelButton: true, confirmButtonColor: '#2e7d32' });
    if (result.isConfirmed) {
      try { await axios.put(`/api/residents/${id}/restore`); Swal.fire('Restored!', '', 'success'); fetchArchivedResidents(); } 
      catch (error) { Swal.fire('Error', 'Failed', 'error'); }
    }
  };

  const handleBulkRestore = async () => {
    if (selectedArchived.length === 0) return;
    const result = await Swal.fire({ title: `Restore ${selectedArchived.length}?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#2e7d32' });
    if (result.isConfirmed) {
        try { await axios.put('/api/residents/restore-multiple', { ids: selectedArchived }); Swal.fire('Success', 'Restored', 'success'); setSelectedArchived([]); fetchArchivedResidents(); } 
        catch (error) { Swal.fire('Error', 'Failed', 'error'); }
    }
  };

  const toggleSelectArchived = (id) => {
    if (selectedArchived.includes(id)) setSelectedArchived(selectedArchived.filter(item => item !== id));
    else setSelectedArchived([...selectedArchived, id]);
  };

  const toggleSelectAllArchived = () => {
    if (selectedArchived.length === currentData.length) setSelectedArchived([]); 
    else setSelectedArchived(currentData.map(r => r._id));
  };

  const handleOpenDocs = (resident) => {
    setSelectedResidentName(`${resident.firstName} ${resident.lastName}`);
    fetchResidentInvoices(resident._id); // Fetch invoices dynamically
    openDoc();
  };

  // FORMAT HELPERS
  const formatMoney = (amount) => Number(amount).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const getInvoiceTypeLabel = (invoice) => {
      if (invoice.type === 'consolidated') return 'Consolidated';
      return invoice.type ? invoice.type.replace('_', ' ') : 'N/A';
  };

  const rows = paginatedData.map((resident) => (
    <Table.Tr key={resident._id}>
      {activeTab === 'archive' && (
          <Table.Td><Checkbox checked={selectedArchived.includes(resident._id)} onChange={() => toggleSelectArchived(resident._id)} /></Table.Td>
      )}
      <Table.Td>
        <Box>
            <Text size="sm" fw={500}>{resident.address.block && resident.address.lot ? `Blk ${resident.address.block} Lot ${resident.address.lot}` : 'No Block/Lot'}</Text>
            <Text size="xs" c="dimmed">No. {resident.address.houseNo || '-'} {resident.address.street}</Text>
        </Box>
      </Table.Td>
      <Table.Td fw={500}>{resident.lastName}, {resident.firstName} {resident.middleName ? `${resident.middleName}` : ''}</Table.Td>
      <Table.Td style={{ textTransform: 'capitalize' }}><Badge color="blue" variant="outline">{resident.type.replace('_', ' ')}</Badge></Table.Td>
      <Table.Td><Badge color={resident.status === 'good_standing' ? 'green' : 'red'} variant="light">{resident.status === 'good_standing' ? 'Good Standing' : 'Delinquent'}</Badge></Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Tooltip label="View Billing History"><ActionIcon color="violet" variant="light" onClick={() => handleOpenDocs(resident)}><IconFolder size={18} /></ActionIcon></Tooltip>
          {activeTab === 'active' ? (
            <>
                <Tooltip label="Edit"><ActionIcon color="blue" variant="light" onClick={() => handleOpenModal(resident)}><IconEdit size={18} /></ActionIcon></Tooltip>
                <Tooltip label="Archive"><ActionIcon color="orange" variant="light" onClick={() => handleArchive(resident._id)}><IconArchive size={18} /></ActionIcon></Tooltip>
            </>
          ) : (
            <Tooltip label="Restore"><ActionIcon color="green" variant="light" onClick={() => handleRestore(resident._id)}><IconRotate2 size={18} /></ActionIcon></Tooltip>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Layout>
      <Group justify="space-between" mb="lg">
        <Title order={2}>Resident Management</Title>
        {activeTab === 'active' && <Button leftSection={<IconPlus size={18} />} color="teal" onClick={() => handleOpenModal()}>Add Resident</Button>}
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab} color="teal" mb="md">
        <Tabs.List>
          <Tabs.Tab value="active" leftSection={<IconEdit size={14} />}>Active Residents</Tabs.Tab>
          <Tabs.Tab value="archive" leftSection={<IconArchive size={14} />}>Archived</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="active" pt="xs">
            <Paper p="md" mb="lg" withBorder shadow="xs">
                {/* ALIGNED TO LEFT (flex-start) */}
                <Group justify="flex-start" align="center" gap="sm">
                    <Group gap="xs">
                        <IconFilter size={20} color="gray" />
                        <Text size="sm" fw={500}>Filters:</Text>
                    </Group>
                    <TextInput placeholder="Search Name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} leftSection={<IconSearch size={16} />} style={{ width: 200 }} />
                    <Select placeholder="Filter by Street" value={filterStreet} onChange={setFilterStreet} data={streetOptions} clearable searchable style={{ width: 180 }} />
                    <Select placeholder="Account Type" value={filterType} onChange={setFilterType} data={['homeowner', 'tenant', 'lot_owner']} clearable style={{ width: 150 }} />
                    <Select placeholder="Status" value={filterStatus} onChange={setFilterStatus} data={['good_standing', 'delinquent']} clearable style={{ width: 150 }} />
                </Group>
            </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="archive" pt="xs">
            <Group justify="space-between" mb="md">
                <Checkbox label="Select All Archived" onChange={toggleSelectAllArchived} checked={currentData.length > 0 && selectedArchived.length === currentData.length} indeterminate={selectedArchived.length > 0 && selectedArchived.length < currentData.length} />
                <Button leftSection={<IconRotate2 size={16} />} color="green" disabled={selectedArchived.length === 0} onClick={handleBulkRestore}>Restore Selected ({selectedArchived.length})</Button>
            </Group>
        </Tabs.Panel>
      </Tabs>

      <Paper withBorder shadow="sm" radius="md">
        {/* ADDED MIN-WIDTH FOR SCROLLING */}
        <ScrollArea h={500}>
          <Table striped highlightOnHover verticalSpacing="sm" style={{ minWidth: '800px' }}>
            <Table.Thead>
              <Table.Tr>
                {activeTab === 'archive' && <Table.Th style={{ width: 40 }}></Table.Th>}
                <Table.Th>Address</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows.length > 0 ? rows : (<Table.Tr><Table.Td colSpan={activeTab === 'archive' ? 6 : 5} align="center">No records found.</Table.Td></Table.Tr>)}</Table.Tbody>
          </Table>
        </ScrollArea>
        {/* PAGINATION */}
        <Group justify="flex-end" p="md" style={{ borderTop: '1px solid #eee' }}>
            <Pagination 
                total={Math.ceil(currentData.length / ITEMS_PER_PAGE)} 
                value={activePage} 
                onChange={setActivePage} 
                color="teal" 
            />
        </Group>
      </Paper>

      {/* Add/Edit Modal */}
      <Modal opened={opened} onClose={close} title={isEdit ? 'Edit Resident' : 'Add New Resident'} size="lg">
        <LoadingOverlay visible={loading} overlayProps={{ radius: "sm", blur: 2 }} />
        <Grid>
          <Grid.Col span={4}>
            <TextInput required label="Last Name" name="lastName" placeholder="e.g. Dela Cruz" value={formData.lastName} onChange={handleChange} />
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput required label="First Name" name="firstName" placeholder="e.g. Juan" value={formData.firstName} onChange={handleChange} />
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput label="Middle Name" name="middleName" placeholder="e.g. Santos" value={formData.middleName} onChange={handleChange} maxLength={50} />
          </Grid.Col>
          
          <Grid.Col span={3}><TextInput label="Block (Opt)" name="block" placeholder="01" value={formData.block} onChange={handleChange} /></Grid.Col>
          <Grid.Col span={3}><TextInput label="Lot (Opt)" name="lot" placeholder="01" value={formData.lot} onChange={handleChange} /></Grid.Col>
          <Grid.Col span={3}><TextInput required label="House No." name="houseNo" placeholder="01" value={formData.houseNo} onChange={handleChange} /></Grid.Col>
          <Grid.Col span={3}><Select required label="Street" placeholder="Select Street" data={streetOptions} value={formData.street} onChange={(val) => handleSelectChange('street', val)} searchable /></Grid.Col>

          <Grid.Col span={6}><TextInput required label="Contact Number" name="contactNumber" placeholder="09123456789" value={formData.contactNumber} onChange={handleChange} maxLength={11} /></Grid.Col>
          <Grid.Col span={6}><TextInput required label="Email" name="email" placeholder="owner@example.com" value={formData.email} onChange={handleChange} /></Grid.Col>
          
          {isEdit && (
            <>
                <Grid.Col span={6}><Select label="Type" value={formData.type} onChange={(val) => handleSelectChange('type', val)} data={[{ value: 'homeowner', label: 'Homeowner' }, { value: 'tenant', label: 'Tenant' }, { value: 'lot_owner', label: 'Lot Owner' }]} /></Grid.Col>
                <Grid.Col span={6}><Select label="Status" value={formData.status} onChange={(val) => handleSelectChange('status', val)} data={[{ value: 'good_standing', label: 'Good Standing' }, { value: 'delinquent', label: 'Delinquent' }]} /></Grid.Col>
            </>
          )}
        </Grid>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={close}>Cancel</Button>
          {isEdit ? (<Button onClick={() => handleSubmit()} color="teal">Update</Button>) : (<Button onClick={handleSendOtp} color="teal" loading={isOtpSending} leftSection={<IconMail size={16} />}>Verify & Save</Button>)}
        </Group>
      </Modal>

      {/* OTP Modal */}
      <Modal opened={otpOpened} onClose={closeOtp} title="Verify Email Address" centered zIndex={1000}>
        <Text size="sm" c="dimmed" mb="md" ta="center">We sent a 6-digit code to <b>{formData.email}</b>. Please enter it below to confirm registration.</Text>
        <Center><PinInput length={6} type="number" size="lg" value={otpInput} onChange={setOtpInput} autoFocus /></Center>
        <Group justify="center" mt="xl"><Button variant="subtle" onClick={handleSendOtp} disabled={isOtpSending}>Resend Code</Button><Button color="teal" onClick={handleVerifyOtpAndSubmit}>Confirm</Button></Group>
      </Modal>

      {/* Documents Modal */}
      <Modal opened={docOpened} onClose={closeDoc} title={`Billing History: ${selectedResidentName}`} size="xl" centered>
        <ScrollArea h={400}>
            {invoicesLoading ? (
                <Center h={200}><Loader color="teal" /></Center>
            ) : residentInvoices.length > 0 ? (
                // Added minWidth here for billing history modal responsiveness
                <Table striped highlightOnHover style={{ minWidth: '600px' }}>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Date</Table.Th>
                            <Table.Th>Type</Table.Th>
                            <Table.Th>Description</Table.Th>
                            <Table.Th>Amount Due</Table.Th>
                            <Table.Th>Paid</Table.Th>
                            <Table.Th>Status</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {residentInvoices.map((inv) => (
                            <Table.Tr key={inv._id}>
                                <Table.Td>{new Date(inv.createdAt).toLocaleDateString()}</Table.Td>
                                <Table.Td><Badge color="gray" variant="outline" size="sm">{getInvoiceTypeLabel(inv)}</Badge></Table.Td>
                                <Table.Td style={{ maxWidth: '200px' }}><Text truncate title={inv.description}>{inv.description}</Text></Table.Td>
                                <Table.Td fw={700}>₱{formatMoney(inv.totalAmount)}</Table.Td>
                                <Table.Td>₱{formatMoney(inv.amountPaid || 0)}</Table.Td>
                                <Table.Td><Badge color={inv.status === 'paid' ? 'green' : inv.status === 'pending_approval' ? 'orange' : 'red'}>{inv.status.toUpperCase()}</Badge></Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            ) : (
                <Box ta="center" py="xl">
                    <IconMoodEmpty size={40} color="gray" style={{ opacity: 0.5, marginBottom: 10 }} />
                    <Text c="dimmed" size="sm">No invoice records found for this resident.</Text>
                </Box>
            )}
        </ScrollArea>
        <Group justify="flex-end" mt="md"><Button onClick={closeDoc} variant="default">Close</Button></Group>
      </Modal>
    </Layout>
  );
};

export default Residents;